/**
 * useGraphData - Hook for transforming optimization results to graph-ready data
 *
 * Handles transformation of raw optimization results into formats expected
 * by PowerGraph and other visualization components.
 *
 * @module hooks/useGraphData
 */

import { useMemo } from 'react';
import { reoptToDailySeries } from '../reoptTransform';
import { simulateDailyResilience } from '../resilienceSimulation';

/**
 * Transform optimization results into graph-ready data
 *
 * @param {Object} params
 * @param {Object|null} params.results - Optimization results from solver
 * @param {number} params.dayIndex - Day index to display (0-364)
 * @param {number} params.tph - Time periods per hour (typically 1)
 * @param {string} params.loadYear - Year for timestamp calculation
 * @param {boolean} params.showResilientMode - Whether to show resilience simulation
 * @param {number} params.outageStartHour - Hour when simulated outage starts
 * @returns {Object} Graph data and metadata
 */
export function useGraphData({
    results,
    dayIndex,
    tph,
    loadYear,
    showResilientMode = false,
    outageStartHour = 0,
}) {
    // Raw graph data from optimization results
    const { data: rawGraphData, isOffGrid, infeasibilityMetrics } = useMemo(() => {
        return reoptToDailySeries(results, parseInt(dayIndex, 10), tph, loadYear);
    }, [results, dayIndex, tph, loadYear]);

    // Normal graph data with aggregated fields for PowerGraph
    const normalGraphData = useMemo(() => {
        return rawGraphData.map(d => ({
            ...d,
            // Restore aggregated fields expected by PowerGraph
            utility: (d.utility_to_load || 0) + (d.utility_to_bess || 0),
            solar: (d.solar_to_load || 0) + (d.solar_to_bess || 0),
            bess: d.bess_to_load || 0,
            net_utility: ((d.utility_to_load || 0) + (d.utility_to_bess || 0)) -
                ((d.solar_export || 0) + (d.bess_export || 0) + (d.diesel_export || 0) + (d.ng_export || 0)),
            // Ensure gen is aggregated if needed (mostly diesel_to_load is enough but PowerGraph checks 'genset')
            genset: (d.diesel_to_load || 0) + (d.ng_to_load || 0)
        }));
    }, [rawGraphData]);

    // Resilient graph data (on-the-fly simulation)
    const resilientGraphData = useMemo(() => {
        if (!showResilientMode) return null;
        return simulateDailyResilience(
            results,
            parseInt(dayIndex, 10),
            outageStartHour,
            tph
        );
    }, [results, dayIndex, outageStartHour, tph, showResilientMode]);

    // Active graph data based on mode
    const graphData = showResilientMode ? resilientGraphData?.data : normalGraphData;

    return {
        rawGraphData,
        normalGraphData,
        resilientGraphData,
        graphData,
        isOffGrid,
        infeasibilityMetrics,
    };
}

export default useGraphData;
